import * as React from 'react';
import { translate, useCurrentLanguage } from '@avensia/excite';
import { styled } from '@glitz/react';
import RatingStars from './RatingStars';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import BaseImage from 'webapp/components/image';
import { bareListDecorator } from 'webapp/decorators/list';
import { ReviewImageFullscreenGallery } from './ReviewImageFullscreenGallery';
import { Button } from 'webapp/components/button';
import { DownVote, Report, UpVote } from 'webapp/components/icons';
import { FieldSize } from 'webapp/components/field-size';
import { darkGrey } from 'webapp/shared/sharedTheme';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { ReportReview } from './ReportReview';
import { Theme } from 'webapp/theme';

export interface TestFreaksReview {
  type: string;
  author: string;
  score: number;
  score_max: number;
  date: string;
  extract: string;
  pros: string;
  cons: string;
  lang: string;
  verified_buyer: boolean;
  votes_up: number;
  votes_down: number;
  product: string;
  feedback_url: string;
  responses: string[];
  images: TestFreakImage[];
  question_answers: string[];
  original: TestFreaksReview;
}

interface TestFreakImage {
  icon: string;
  icon_height: number;
  icon_width: number;
  large: string;
  large_height: number;
  large_width: number;
  medium: string;
  medium_height: number;
  medium_width: number;
}

type PropType = {
  review: TestFreaksReview;
};

enum ImageSize {
  Icon = 'icon',
  Medium = 'medium',
  Large = 'large',
}

function mapImages(images: TestFreakImage[], size: ImageSize) {
  switch (size) {
    case 'icon':
      return images.map(image => ({ url: image.icon, width: image.icon_width, height: image.icon_height }));
    case 'medium':
      return images.map(image => ({ url: image.medium, width: image.medium_width, height: image.medium_height }));
    case 'large':
      return images.map(image => ({ url: image.large, width: image.large_width, height: image.large_height }));
    default:
      return images.map(image => ({ url: image.icon, width: image.icon_width, height: image.icon_height }));
  }
}

export const Review = ({ review }: PropType) => {
  const [opengallery, setOpenGallery] = React.useState<boolean>(false);
  const [translated, setTranslated] = React.useState(Boolean(review.original));
  const [upVote, setUpVote] = React.useState(false);
  const [downVote, setDownVote] = React.useState(false);
  const [showReportForm, setShowReportForm] = React.useState(false);
  const currentLanguage = useCurrentLanguage();
  const languageNames = new Intl.DisplayNames([currentLanguage], { type: 'language' });
  const text = translated ? review.extract : review.original?.extract ?? review.extract;
  const pros = translated ? review.pros : review.original?.pros ?? review.pros;
  const cons = translated ? review.cons : review.original?.cons ?? review.cons;
  const upVotes = review.votes_up >= 0 ? review.votes_up : 0;
  const downVotes = review.votes_down >= 0 ? review.votes_down : 0;

  const upVoteReview = async (upVote: boolean) => {
    try {
      await fetch(`${review.feedback_url}?type=${upVote ? 'up' : 'reset'}`);
      setUpVote(upVote);
    } catch (e) {
      console.warn("couldn't update up vote", e);
    }
  };

  const downVoteReview = async (downVote: boolean) => {
    try {
      await fetch(`${review.feedback_url}?type=${downVote ? 'down' : 'reset'}`);
      setDownVote(downVote);
    } catch (e) {
      console.warn("couldn't update down vote", e);
    }
  };

  return (
    <Base key={review.date} lang={review.lang}>
      <RatingStars rating={review.score} />
      <Date dateTime={review.date}>{review.date}</Date>
      <Text>
        <p>{text}</p>
        {review.pros && (
          <div>
            <b>{translate(t => t.productPage.reviewsPros)}</b> {pros}
          </div>
        )}
        {review.cons && (
          <div>
            <b>{translate(t => t.productPage.reviewsCons)}</b> {cons}
          </div>
        )}
        {review.images.length > 0 && (
          <>
            <Images>
              {mapImages(review.images, ImageSize.Icon).map(image => (
                <li key={image.url}>
                  <Image image={image} onClick={() => setOpenGallery(true)} />
                </li>
              ))}
            </Images>
            <ReviewImageFullscreenGallery
              images={mapImages(review.images, ImageSize.Medium)}
              isOpen={opengallery}
              setIsOpen={setOpenGallery}
              startSlide={0}
            />
          </>
        )}
      </Text>
      <Author>{review.author}</Author>
      <Bottom>
        {review.original && (
          <Translation>
            {translated && (
              <>
                <span>
                  {translate(t => t.productPage.reviewsTranslatedFrom, {
                    lang: languageNames.of(review.original?.lang) ?? review.original?.lang,
                  })}
                </span>
                <span> • </span>
              </>
            )}
            <TranslateToggleButton
              as={Theme.Transparent}
              size={FieldSize.XSmall}
              onClick={() => setTranslated(!translated)}
            >
              {translated
                ? translate(t => t.productPage.reviewsShowOriginal)
                : translate(t => t.productPage.reviewsTranslate)}
            </TranslateToggleButton>
          </Translation>
        )}
        <Actions>
          <Action
            as={Theme.Transparent}
            size={FieldSize.XSmall}
            square
            disableRippleEffect
            onClick={() => upVoteReview(!upVote)}
            aria-label={translate(t => t.productPage.reviewsUpVote)}
            title={translate(t => t.productPage.reviewsUpVote)}
          >
            <UpVoteIcon /> {upVote ? upVotes + 1 : upVotes}
          </Action>
          <Action
            as={Theme.Transparent}
            size={FieldSize.XSmall}
            square
            disableRippleEffect
            onClick={() => downVoteReview(!downVote)}
            aria-label={translate(t => t.productPage.reviewsDownVote)}
            title={translate(t => t.productPage.reviewsDownVote)}
          >
            <DownVoteIcon /> {downVote ? downVotes + 1 : downVotes}
          </Action>
          <Action
            as={Theme.Transparent}
            size={FieldSize.XSmall}
            square
            disableRippleEffect
            onClick={() => setShowReportForm(!showReportForm)}
            aria-label={translate(t => t.productPage.reviewsReport)}
            title={translate(t => t.productPage.reviewsReport)}
          >
            <Report />
          </Action>
        </Actions>
      </Bottom>
      <ReportReview
        feedbackUrl={review.feedback_url}
        showReportForm={showReportForm}
        setShowReportForm={setShowReportForm}
      />
    </Base>
  );
};

const Base = styled.li(
  createMaxSmallViewportDecorator({
    padding: { x: t => t.sharedSpacing.m },
  }),
  {
    padding: { left: t => t.sharedSpacing.xxl },
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    columnGap: t => t.sharedSpacing.m,
    rowGap: t => t.sharedSpacing.xs,
  },
);

const Text = styled.p(
  createMaxSmallViewportDecorator({
    gridColumnStart: 'span 2',
  }),
  {
    marginBottom: 0,
  },
);

const Images = styled.ul(bareListDecorator, {
  marginTop: t => t.sharedSpacing.m,
  display: 'flex',
  gap: t => t.sharedSpacing.xs,
});

const Image = styled(BaseImage, {
  cursor: 'pointer',
});

const Author = styled.span(
  createMinLargeViewportDecorator({ textAlign: 'right' }),
  createMaxSmallViewportDecorator({ fontWeight: 'bold' }),
);
const Date = styled.time({
  textAlign: 'right',
});

const Bottom = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  width: '100%',
  gridColumnStart: 'span 2',
  alignItems: 'center',
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: t => t.sharedSpacing.xxs,
  marginLeft: 'auto',
});

const Action = styled(Button, {
  gap: t => t.sharedSpacing.xxs,
  color: darkGrey,
  fontSize: t => t.sharedFontSize.xxs,
});

const TranslateToggleButton = styled(
  Button,
  createButtonHoverDecorator({
    textDecoration: 'underline',
  }),
  {
    padding: { xy: 0 },
  },
);

const Translation = styled.div({
  display: 'flex',
  fontSize: t => t.sharedFontSize.xxs,
  alignItems: 'center',
  gap: t => t.sharedSpacing.xs,
  color: darkGrey,
});

const UpVoteIcon = styled(UpVote, {
  marginBottom: 4,
});

const DownVoteIcon = styled(DownVote, {
  marginTop: 4,
});
